import { DocumentData, QueryDocumentSnapshot } from "@angular/fire/firestore"
import { first, last } from "lodash-es"
import { StudyOrderProgress as BackendStudyOrderProgress } from '@longtail-education/superprepstudy-data-model-frontend'
import { DeckMode, StudyOrder } from "@longtail-education/superprep-data-model-frontend"
import { groupBy, localeCompare, sortByArray, toUniqueNonNullableArray } from "@nx-superprep/utils"
import { StudyOrderTemplate } from "./study-order-template"
import { CardRequest } from "../../services"

interface CardSearchOptions {
  startAt: number,
  deckMode: DeckMode,
  cardCount: number,
  ascending: boolean,
}

// interfaces for better type safety
interface FindResult {
  readonly elementIndex: number,
  readonly cardIndex: number,
}

interface MoveResult {
  destinationIndex: number,
  success: boolean,
}

/**
 * Custom error class for StudyOrderProgress operations
 */
class StudyOrderError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'StudyOrderError';
  }
}

/**
 * Represents the progress of a study order.
 */
export class StudyOrderProgress extends BackendStudyOrderProgress {
  /** The module ID associated with the study order progress. */
  declare readonly moduleId: string

  // Cache for performance optimization
  private cardIdCache: Map<string, Set<string>> = new Map();

  public clearCache(): void {
    this.cardIdCache.clear()
  }

  public dispose(): void {
    this.clearCache()
    this.remainingStudyOrders = []
    this.finishedStudyOrders = []
  }

  public debug(): {
    cacheSize: number,
    remainingCount: number,
    finishedCount: number,
  } {
    return {
      cacheSize: this.cardIdCache.size,
      remainingCount: this.remainingStudyOrders.length,
      finishedCount: this.finishedStudyOrders.length
    }
  }

  /**
   * Checks whether the study order progress is valid.
   */
  get isValid(): boolean {
    return this.remainingStudyOrders.length > 0;
  }

  constructor(
    moduleId: string,
    remainingStudyOrders: StudyOrder[] = [],
    finishedStudyOrders: StudyOrder[] = [],
    template?: StudyOrderTemplate,
  ) {
    if (!moduleId) {
      throw new StudyOrderError('Module ID is required', 'MISSING_MODULE_ID');
    }
    if (!Array.isArray(remainingStudyOrders) || !Array.isArray(finishedStudyOrders)) {
      throw new StudyOrderError('Study orders must be arrays', 'INVALID_STUDY_ORDERS');
    }

    super(moduleId, remainingStudyOrders, finishedStudyOrders);

    if (template) {
      if (template.id !== moduleId) {
        throw new StudyOrderError('Template ID does not match module ID', 'TEMPLATE_MISMATCH');
      }
      this.finishedStudyOrders = StudyOrderProgress.cleanup(finishedStudyOrders);
      this.remainingStudyOrders = StudyOrderProgress.cleanup(remainingStudyOrders, this.finishedStudyOrders, template?.studyOrders);
    }
    // Initialize cache
    this.updateCache();
  }

  private updateCache(): void {
    this.cardIdCache.clear()
    const allOrders = [...this.remainingStudyOrders, ...this.finishedStudyOrders]

    // Pre-allocate approximate size
    this.cardIdCache = new Map(allOrders.map(order => [order.fullPathElementId, new Set(order.cardIds)]))
  }

  //for static factory methods
  static createFromTemplate(
    moduleId: string,
    template: StudyOrderTemplate
  ): StudyOrderProgress {
    if (!template) {
      throw new StudyOrderError('Template is required', 'MISSING_TEMPLATE');
    }
    return new StudyOrderProgress(moduleId, [], [], template);
  }

  /**
   * Cleans up study orders by removing duplicates and sorting them.
   */
  static cleanup(
    items: StudyOrder[] = [],
    finished: StudyOrder[] = [],
    template: StudyOrder[] = []
  ): StudyOrder[] {
    return Object.entries(
      groupBy(
        [...template, ...items].filter(item =>
          template.length === 0 || template.some(t => t.fullPathElementId === item.fullPathElementId)
        ),
        'fullPathElementId',
      ),
    )
      .map(([fullPathElementId, studyOrders]) => {
        const matchingTemplate = template.find(t => t.fullPathElementId === fullPathElementId);
        if (!matchingTemplate && template.length > 0) return null;

        return {
          fullPathElementId,
          cardIds: this.processCardIds(studyOrders, finished, matchingTemplate),
        };
      })
      .filter((item): item is StudyOrder => item !== null)
      .sort((a, b) => localeCompare(a.fullPathElementId, b.fullPathElementId));
  }

  private static processCardIds(
    studyOrders: StudyOrder[],
    finished: StudyOrder[],
    template?: StudyOrder
  ): string[] {
    const allCardIds = studyOrders.flatMap(s => s.cardIds);
    const uniqueCardIds = toUniqueNonNullableArray(allCardIds);

    const filteredCardIds = uniqueCardIds.filter(cardId => {
      const isInTemplate = !template || template.cardIds.includes(cardId);
      const isNotFinished = !finished.some(f =>
        f.fullPathElementId === template?.fullPathElementId &&
        f.cardIds.includes(cardId)
      );
      return isInTemplate && isNotFinished;
    });

    return template ? sortByArray(filteredCardIds, template.cardIds) : filteredCardIds;
  }

  /**
   * Moves a card between study order arrays.
   */
  move(id: string, src: StudyOrder[], dst: StudyOrder[]): MoveResult {
    if (!this.validateCardId(id)) {
      return { success: false, destinationIndex: -1 };
    }

    const fullPathElementId = this.extractPathElementId(id);
    if (!fullPathElementId) {
      return { success: false, destinationIndex: -1 };
    }

    const sourceIndices = this.findCardLocation(id, fullPathElementId, src);
    const destIndices = this.findCardLocation(id, fullPathElementId, dst);

    // Handle destination
    const destinationIndex = this.addToDestination(id, fullPathElementId, dst, destIndices);

    // Handle source
    if (this.isCardPresent(sourceIndices)) {
      this.removeFromSource(src, sourceIndices);
    }

    this.updateCache();
    return {
      success: true,
      destinationIndex: destinationIndex
    };
  }

  private validateCardId(id: string): boolean {
    return Boolean(id && typeof id === 'string');
  }

  private extractPathElementId(id: string): string | null {
    const parts = id.split('-');
    return parts.length >= 3 ? `${parts[1]}-${parts[2]}` : null;
  }

  private findCardLocation(
    cardId: string,
    fullPathElementId: string,
    array: StudyOrder[]
  ): FindResult {
    const elementIndex = array.findIndex(
      item => item.fullPathElementId === fullPathElementId
    );

    const cardIndex = elementIndex >= 0
      ? array[elementIndex].cardIds.indexOf(cardId)
      : -1;

    return { elementIndex, cardIndex };
  }

  private isCardPresent(indices: FindResult): boolean {
    return indices.elementIndex >= 0 && indices.cardIndex >= 0;
  }

  private addToDestination(
    cardId: string,
    fullPathElementId: string,
    array: StudyOrder[],
    indices: FindResult
  ): number {
    if (indices.cardIndex >= 0) {
      return indices.elementIndex;
    }

    if (indices.elementIndex >= 0) {
      array[indices.elementIndex].cardIds.push(cardId);
      return indices.elementIndex;
    }

    array.push({
      fullPathElementId,
      cardIds: [cardId]
    });
    return array.length - 1;
  }

  private removeFromSource(array: StudyOrder[], indices: FindResult): void {
    if (this.isCardPresent(indices)) {
      array[indices.elementIndex].cardIds.splice(indices.cardIndex, 1);
    }
  }

  /**
   * Move a card to finished study orders
   */
  moveToFinished(cardId: string): number {
    const result = this.move(
      cardId,
      this.remainingStudyOrders,
      this.finishedStudyOrders
    );
    return result.destinationIndex;
  }

  /**
   * Move a card to remaining study orders
   */
  moveToRemaining(cardId: string): number {
    const result = this.move(
      cardId,
      this.finishedStudyOrders,
      this.remainingStudyOrders
    );
    return result.destinationIndex;
  }

  /**
   * Find next card IDs based on search options
   */
  private findNextCardIds(
    studyOrders: StudyOrder[],
    options: CardSearchOptions
  ): string[] {
    // Cache length check to avoid repeated access
    const length = studyOrders.length;
    if (!this.validateSearchOptions(options, studyOrders) || length === 0) {
      return [];
    }

    const result: string[] = [];
    const visitedIndices = new Set<number>();
    const startAt = options.startAt % length;
    const isDeepMode = options.deckMode === DeckMode.Deep;

    // Pre-calculate the maximum possible iterations
    const maxIterations = isDeepMode ? options.cardCount : Math.min(length, options.cardCount);
    let iterations = 0;

    for (
      let currentIndex = startAt;
      iterations < maxIterations;
      currentIndex = (currentIndex + 1) % length
    ) {
      if (visitedIndices.has(currentIndex) && !isDeepMode) {
        continue;
      }

      iterations++;
      const studyOrder = studyOrders[currentIndex];
      const selectedCardId = options.ascending
        ? first(studyOrder.cardIds)
        : last(studyOrder.cardIds);

      if (selectedCardId) {
        result.push(selectedCardId);
        if (result.length === options.cardCount) break;
      }

      visitedIndices.add(currentIndex);
    }

    return result;
  }

  private validateSearchOptions(
    options: CardSearchOptions,
    studyOrders: StudyOrder[]
  ): boolean {
    if (!options || typeof options.startAt !== 'number' || typeof options.cardCount !== 'number') {
      throw new StudyOrderError('Invalid search options format', 'INVALID_SEARCH_OPTIONS');
    }

    if (options.startAt < 0 || options.cardCount <= 0) {
      throw new StudyOrderError('Invalid search options values', 'INVALID_SEARCH_VALUES');
    }

    if (!Array.isArray(studyOrders)) {
      throw new StudyOrderError('Study orders must be an array', 'INVALID_STUDY_ORDERS');
    }

    return studyOrders.length > 0;
  }

  /**
   * Find next acquisition card IDs
   */
  findNextAcquisitionCardIds(options: CardRequest): string[] {
    if (!this.validateCardRequest(options)) {
      return [];
    }

    if (options.justAcquiredCardId) {
      this.moveToFinished(options.justAcquiredCardId);
    }

    const filteredOrders = this.filterStudyOrders(
      this.remainingStudyOrders,
      options.acquisitionCardDeck
    );

    const startAt = this.calculateStartPosition(
      options.justAcquiredCardId,
      filteredOrders
    );

    return this.findNextCardIds(filteredOrders, {
      startAt,
      deckMode: options.deckMode,
      cardCount: options.cardCount,
      ascending: true
    });
  }

  /**
   * Find next complementary card IDs
   */
  findNextComplementaryCardIds(options: CardRequest): string[] {
    if (!this.validateCardRequest(options)) {
      return [];
    }

    const excludedCardIds = [...options.acquisitionCardDeck];
    if (options.justAcquiredCardId) {
      excludedCardIds.push(options.justAcquiredCardId);
    }

    const shuffledOrders = this.shuffleStudyOrders(
      this.filterStudyOrders(this.finishedStudyOrders, excludedCardIds)
    );

    const startAt = this.calculateComplementaryStartPosition(
      options.justAcquiredCardId,
      shuffledOrders
    );

    return this.findNextCardIds(shuffledOrders, {
      startAt,
      deckMode: options.deckMode,
      cardCount: options.cardCount,
      ascending: false
    });
  }

  private validateCardRequest(options: CardRequest): boolean {
    return Boolean(options && options.studyOrderProgress);
  }

  private filterStudyOrders(
    orders: StudyOrder[],
    excludeCardIds: string[]
  ): StudyOrder[] {
    return orders.filter(order =>
      excludeCardIds.every(cardId => !cardId.includes(order.fullPathElementId))
    );
  }

  private shuffleStudyOrders(orders: StudyOrder[]): StudyOrder[] {
    return orders.map(order => ({
      ...order,
      cardIds: [...order.cardIds].sort(() => Math.random() - 0.5)
    }));
  }

  private calculateStartPosition(
    removedCardId: string | undefined,
    studyOrders: StudyOrder[]
  ): number {
    if (!removedCardId) {
      return 0;
    }

    return Math.max(
      0,
      studyOrders.findIndex(s =>
        removedCardId.includes(s.fullPathElementId)
      )
    );
  }

  private calculateComplementaryStartPosition(
    removedCardId: string | undefined,
    studyOrders: StudyOrder[]
  ): number {
    if (!removedCardId) {
      return 0;
    }

    const found = studyOrders.findIndex(s =>
      removedCardId.includes(s.fullPathElementId)
    );

    return found >= 0 ? (found + 1) % studyOrders.length : 0;
  }

  // Firestore converter methods remain unchanged
  static override converter = {
    toFirestore(data: StudyOrderProgress) {
      data.remainingStudyOrders = StudyOrderProgress.cleanup(data.remainingStudyOrders);
      data.finishedStudyOrders = StudyOrderProgress.cleanup(data.finishedStudyOrders);
      return BackendStudyOrderProgress.converter.toFirestore(data);
    },
    fromFirestore(snapshot: QueryDocumentSnapshot): StudyOrderProgress {
      const obj = BackendStudyOrderProgress.converter.fromFirestore(snapshot);
      return new StudyOrderProgress(
        obj.moduleId ?? '',
        obj.remainingStudyOrders,
        obj.finishedStudyOrders
      );
    },
    fromFirestoreDocumentData(
      documentData: DocumentData,
      template: DocumentData,
    ): StudyOrderProgress {
      return new StudyOrderProgress(
        documentData['id'] ?? documentData['moduleId'],
        documentData['remainingStudyOrders'],
        documentData['finishedStudyOrders'],
        StudyOrderTemplate.converter.fromFirestoreDocumentData(template),
      );
    },
  }
}
